// Tela de Cadastro (/signup) — mockup "Cadastro": split hero/formulário, com
// os três passos no painel esquerdo (derivados dos campos preenchidos, não é
// um wizard), medidor de força da senha e planos em cards.
//
// Whitelabel: mesma regra do Login — sem imagem configurada aparece o hero
// inteiro; com imagem, ela vira o fundo com véu e só logo + rodapé.
import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import qs from "query-string";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";

import CheckIcon from "@material-ui/icons/Check";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import ColorModeContext from "../../layout/themeContext";
import usePlans from "../../hooks/usePlans";
import useSettings from "../../hooks/useSettings";
import { getBackendUrl } from "../../config";
import useAerosAuthStyles from "../../styles/aerosAuthStyles";
import AuthBrand from "../../components/AuthBrand";
import { periodoLabel } from "../Plans/planRules";
import { precosExibiveis } from "../../components/PlansManager/planPriceRules";
import {
  passwordStrength,
  signupStepStates,
  documentKind,
  formatDocument,
  onlyDigits,
} from "../Login/authRules";

const TERMOS_DE_USO_URL = "https://www.talkpulse.com.br/termos-de-uso/";
const POLITICA_DE_PRIVACIDADE_URL = "https://www.talkpulse.com.br/politica-de-privacidade/";

const UserSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
  companyName: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  document: Yup.string()
    .test("document-format", "CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos", function (value) {
      if (!value) return false;
      const numbers = onlyDigits(value);
      return numbers.length === 11 || numbers.length === 14;
    })
    .required("CPF ou CNPJ é obrigatório"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  phone: Yup.string().required("Required"),
});

// Validação de CPF (dígitos verificadores). O CNPJ é conferido no backend,
// que consulta a Receita e devolve a razão social.
const validateCpf = (cpf) => {
  const cleanCpf = onlyDigits(cpf);
  if (cleanCpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cleanCpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.charAt(10))) return false;

  return true;
};

const SignUp = () => {
  const c = useAerosAuthStyles();
  const history = useHistory();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName, mode } = colorMode;
  const { getPlanList } = usePlans();
  const { getPublicSetting } = useSettings();

  const [plans, setPlans] = useState([]);
  const [validatingDocument, setValidatingDocument] = useState(false);
  const [documentOk, setDocumentOk] = useState(false);
  const [lastValidatedDocument, setLastValidatedDocument] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [backgroundLight, setBackgroundLight] = useState("");
  const [backgroundDark, setBackgroundDark] = useState("");

  let companyId = null;
  const params = qs.parse(window.location.search);
  if (params.companyId !== undefined) {
    companyId = params.companyId;
  }

  const initialState = {
    name: "",
    email: "",
    password: "",
    phone: "",
    companyId,
    companyName: "",
    document: "",
    planId: "",
  };

  useEffect(() => {
    getPublicSetting("userCreation")
      .then((data) => {
        if (data === "disabled") {
          toast.error(i18n.t("signup.toasts.disabled"));
          history.push("/login");
        }
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });

    getPublicSetting("appLogoBackgroundLight", companyId)
      .then((bgLight) => {
        setBackgroundLight(bgLight ? `${getBackendUrl()}/public/${bgLight}` : "");
      })
      .catch(() => setBackgroundLight(""));

    getPublicSetting("appLogoBackgroundDark", companyId)
      .then((bgDark) => {
        setBackgroundDark(bgDark ? `${getBackendUrl()}/public/${bgDark}` : "");
      })
      .catch(() => setBackgroundDark(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, companyId]);

  useEffect(() => {
    const fetchData = async () => {
      const planList = await getPlanList({ listPublic: "false" });
      setPlans(planList);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateDocument = async (document, setFieldValue) => {
    const cleanDocument = onlyDigits(document);
    if (lastValidatedDocument === cleanDocument) return;

    const kind = documentKind(document);
    if (!kind) {
      setDocumentOk(false);
      return;
    }

    if (kind === "cpf") {
      const ok = validateCpf(document);
      setLastValidatedDocument(cleanDocument);
      setDocumentOk(ok);
      if (ok) toast.success(i18n.t("signup.documentValidCpf"));
      else toast.error(i18n.t("signup.toasts.documentInvalid"));
      return;
    }

    setValidatingDocument(true);
    try {
      const { data } = await openApi.post("/auth/validate-cnpj", {
        cnpj: cleanDocument,
      });
      setValidatingDocument(false);
      setLastValidatedDocument(cleanDocument);

      if (data.valid && data.data?.nome) {
        if (data.data.tipo === "cnpj") {
          setFieldValue("companyName", data.data.nome);
        }
        setDocumentOk(true);
        toast.success(i18n.t("signup.documentValidCnpj"));
      } else {
        setDocumentOk(false);
        toast.error(i18n.t("signup.toasts.documentNotFound"));
      }
    } catch (error) {
      setValidatingDocument(false);
      setLastValidatedDocument(cleanDocument);
      setDocumentOk(false);
      toast.error(i18n.t("signup.toasts.documentError"));
    }
  };

  const handleSignUp = async (values) => {
    try {
      await openApi.post("/auth/signup", values);
      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
  };

  const heroImage =
    mode === "light" ? backgroundLight?.trim() : backgroundDark?.trim();
  const hasHeroImage = Boolean(heroImage);

  const renderHero = (values) => {
    const steps = signupStepStates(values);
    return (
      <div
        className={c.hero}
        style={hasHeroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
      >
        {hasHeroImage ? (
          <div className={c.heroVeil} />
        ) : (
          <>
            <div className={c.heroBlobCyan} style={{ left: -240, top: -120, right: "auto", bottom: "auto" }} />
            <div className={c.heroBlobPurple} style={{ right: -200, bottom: -160, left: "auto", top: "auto" }} />
          </>
        )}

        <AuthBrand classes={c} appName={appName} appLogoFavicon={appLogoFavicon} />

        {/* Texto e passos ficam mesmo com imagem de whitelabel (ver Login). */}
        <div className={c.heroBody} style={{ gap: 24 }}>
          <div className={`${c.heroTitle} ${c.heroTitleSmall}`}>
            {i18n.t("signup.hero.title")}
          </div>
          <div className={c.steps}>
            {steps.map((step) => (
              <div key={step.n} className={c.step}>
                <div
                  className={`${c.stepDot} ${
                    step.state === "done"
                      ? c.stepDone
                      : step.state === "current"
                      ? c.stepCurrent
                      : c.stepUpcoming
                  }`}
                >
                  {step.state === "done" ? <CheckIcon /> : step.n}
                </div>
                <div>
                  <div
                    className={`${c.stepTitle} ${
                      step.state === "upcoming" ? c.stepTitleUpcoming : ""
                    }`}
                  >
                    {step.title}
                  </div>
                  <div className={c.stepDesc}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={c.heroFooter}>
          {i18n.t("signup.alreadyHave")}{" "}
          <RouterLink to="/login" className={c.ghostLink}>
            {i18n.t("signup.goLogin")}
          </RouterLink>
        </div>
      </div>
    );
  };

  const field = (name, labelKey, { touched, errors, required = true, ...extra } = {}) => {
    const error = touched[name] && errors[name];
    return (
      <div>
        <label className={c.label} htmlFor={name}>
          {i18n.t(labelKey)}
          {required && <span className={c.labelRequired}>*</span>}
        </label>
        <Field
          as={TextField}
          id={name}
          name={name}
          variant="outlined"
          className={c.input}
          error={Boolean(error)}
          helperText={error || ""}
          {...extra}
        />
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{appName || "Pulse - Cadastro"}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>

      <Formik
        initialValues={initialState}
        enableReinitialize={true}
        validationSchema={UserSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSignUp(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue }) => {
          const strength = passwordStrength(values.password);
          return (
            <div className={`${c.root} ${c.heroNarrow}`}>
              {renderHero(values)}

              <div className={c.formPane}>
                <Form className={`${c.formCard} ${c.formCardWide}`} noValidate>
                  <div>
                    <div className={`${c.formTitle} ${c.formTitleSmall}`}>
                      {i18n.t("signup.pageTitle")}
                    </div>
                    <div className={c.formSubtitle}>
                      {i18n.t("signup.pageSubtitle")}
                    </div>
                  </div>

                  {/* documento + razão social */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0,1fr) minmax(0,1.5fr)",
                      gap: 12,
                    }}
                  >
                    <div>
                      <label className={c.label} htmlFor="document">
                        {i18n.t("signup.form.document")}
                        <span className={c.labelRequired}>*</span>
                      </label>
                      <Field
                        as={TextField}
                        id="document"
                        name="document"
                        variant="outlined"
                        className={`${c.input} ${documentOk ? c.inputValid : ""}`}
                        error={touched.document && Boolean(errors.document)}
                        helperText={(touched.document && errors.document) || ""}
                        onChange={(e) => {
                          const formatted = formatDocument(e.target.value);
                          setFieldValue("document", formatted);
                          setDocumentOk(false);
                        }}
                        onBlur={(e) => validateDocument(e.target.value, setFieldValue)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {validatingDocument ? (
                                <CircularProgress size={17} />
                              ) : documentOk ? (
                                <CheckCircleIcon style={{ color: "#34D399" }} />
                              ) : null}
                            </InputAdornment>
                          ),
                        }}
                      />
                      {documentOk && (
                        <div className={c.fieldHintOk}>
                          {documentKind(values.document) === "cnpj"
                            ? i18n.t("signup.documentValidCnpj")
                            : i18n.t("signup.documentValidCpf")}
                        </div>
                      )}
                      {validatingDocument && (
                        <div className={c.formSubtitle} style={{ fontSize: 11 }}>
                          {i18n.t("signup.documentValidating")}
                        </div>
                      )}
                    </div>

                    {field("companyName", "signup.form.companyName", { touched, errors })}
                  </div>

                  <div className={c.planGrid} style={{ gridTemplateColumns: "1fr 1fr" }}>
                    {field("name", "signup.form.yourName", { touched, errors })}
                    {field("phone", "signup.form.whatsapp", {
                      touched,
                      errors,
                      placeholder: "(31) 98877-1234",
                    })}
                  </div>

                  <div className={c.planGrid} style={{ gridTemplateColumns: "1fr 1fr" }}>
                    {field("email", "signup.form.email", {
                      touched,
                      errors,
                      type: "email",
                      autoComplete: "email",
                    })}

                    <div>
                      <label className={c.label} htmlFor="password">
                        {i18n.t("signup.form.password")}
                        <span className={c.labelRequired}>*</span>
                      </label>
                      <Field
                        as={TextField}
                        id="password"
                        name="password"
                        variant="outlined"
                        className={c.input}
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        error={touched.password && Boolean(errors.password)}
                        helperText={(touched.password && errors.password) || ""}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                aria-label={i18n.t(
                                  showPassword ? "login.hidePassword" : "login.showPassword"
                                )}
                                onClick={() => setShowPassword((v) => !v)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <div className={c.strengthTrack} aria-hidden="true">
                        {[1, 2, 3, 4].map((n) => (
                          <div
                            key={n}
                            className={c.strengthSegment}
                            style={
                              n <= strength.score
                                ? {
                                    backgroundColor:
                                      strength.score >= 4
                                        ? "#34D399"
                                        : strength.score === 3
                                        ? "#22D3EE"
                                        : strength.score === 2
                                        ? "#FBBF24"
                                        : "#F87171",
                                  }
                                : undefined
                            }
                          />
                        ))}
                      </div>
                      {values.password && (
                        <div className={c.strengthLabel}>{strength.label}</div>
                      )}
                    </div>
                  </div>

                  {/* planos */}
                  <div>
                    <span className={c.label}>
                      {i18n.t("signup.form.plan")}
                      <span className={c.labelRequired}>*</span>
                    </span>
                    <div className={c.planGrid}>
                      {plans.map((plan) => {
                        const on = String(values.planId) === String(plan.id);
                        return (
                          <button
                            key={plan.id}
                            type="button"
                            className={`${c.planCard} ${on ? c.planCardOn : ""}`}
                            aria-pressed={on}
                            onClick={() => setFieldValue("planId", plan.id)}
                          >
                            {on && (
                              <span className={c.planCheck}>
                                <CheckIcon />
                              </span>
                            )}
                            <div className={c.planName}>{plan.name}</div>
                            {precosExibiveis(plan).map((p) => (
                              <div className={c.planPrice} key={p.period}>
                                {p.label}
                                <span className={c.planPer}>
                                  {periodoLabel(p.period)}
                                </span>
                              </div>
                            ))}
                            <div className={c.planMeta}>
                              {i18n.t("signup.plan.meta", {
                                users: plan.users,
                                connections: plan.connections,
                                queues: plan.queues,
                              })}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
                    <div className={c.terms}>
                      {i18n.t("signup.termsPrefix")}
                      <a
                        href={TERMOS_DE_USO_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {i18n.t("signup.termsLink")}
                      </a>
                      {i18n.t("signup.termsAnd")}
                      <a
                        href={POLITICA_DE_PRIVACIDADE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {i18n.t("signup.privacyLink")}
                      </a>
                      {i18n.t("signup.termsSuffix")}
                    </div>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      className={c.gradientButton}
                      style={{ padding: "12px 22px", whiteSpace: "nowrap" }}
                      disabled={validatingDocument || isSubmitting}
                      endIcon={<ArrowForwardIcon />}
                    >
                      {i18n.t("signup.submit")}
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default SignUp;
